import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { LiveProxyClient } from "../lib/live-proxy-client";
import type { ConnectionStatus } from "../lib/live-proxy-client";
import { AudioStreamer, audioContext, disposeAudioContext, unlockAudioContext, VOL_METER_WORKLET_SRC } from "../lib/audio";

export type UseLiveAPIResults = {
  client: LiveProxyClient;
  connected: boolean;
  status: ConnectionStatus;
  volume: number;
  unlockAudio: () => void;
};

export function useLiveAPI(): UseLiveAPIResults {
  const clientRef = useRef<LiveProxyClient | null>(null);
  if (!clientRef.current) clientRef.current = new LiveProxyClient();
  const client = clientRef.current;

  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [volume, setVolume] = useState(0);

  const ensureStreamer = useCallback((audioCtx: AudioContext) => {
    if (audioStreamerRef.current) return;
    audioStreamerRef.current = new AudioStreamer(audioCtx);
    audioStreamerRef.current
      .addWorklet<any>("vumeter-out", VOL_METER_WORKLET_SRC, (ev: any) => {
        setVolume(ev.data.volume);
      })
      .catch((e: unknown) => {
        Sentry.captureException(e, { tags: { flow: "audio", event: "vumeter-worklet-init" } });
      });
  }, []);

  // Audio output setup (deferred; on mobile this resolves outside a gesture,
  // so unlockAudio below is the path that actually activates audio there)
  useEffect(() => {
    let cancelled = false;
    audioContext({ id: "audio-out" })
      .then((audioCtx: AudioContext) => {
        if (!cancelled) ensureStreamer(audioCtx);
      })
      .catch((e: unknown) => {
        Sentry.captureException(e, { tags: { flow: "audio", event: "audio-context-init" } });
      });
    return () => { cancelled = true; };
  }, [ensureStreamer]);

  // Call synchronously from a user-gesture handler (e.g. the Start button):
  // creates/resumes the output context within the gesture so iOS allows playback.
  const unlockAudio = useCallback(() => {
    try {
      ensureStreamer(unlockAudioContext("audio-out"));
    } catch (e: unknown) {
      Sentry.captureException(e, { tags: { flow: "audio", event: "audio-unlock" } });
    }
  }, [ensureStreamer]);

  // iOS: resume AudioContext when tab returns to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        audioStreamerRef.current?.context.resume()?.catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Wire client events → audio streamer + connection status
  useEffect(() => {
    const onStatus   = (s: ConnectionStatus) => setStatus(s);
    const onClose    = () => audioStreamerRef.current?.stop();
    const stopAudio  = () => audioStreamerRef.current?.stop();
    const onAudio    = (data: ArrayBuffer) => audioStreamerRef.current?.addPCM16(new Uint8Array(data));

    client
      .on("status", onStatus)
      .on("close", onClose)
      .on("interrupted", stopAudio)
      .on("audio", onAudio);


    return () => {
      audioStreamerRef.current?.stop();
      audioStreamerRef.current = null;
      disposeAudioContext("audio-out");
      client
        .off("status", onStatus)
        .off("close", onClose)
        .off("interrupted", stopAudio)
        .off("audio", onAudio)
        .disconnect();
    };
  }, [client]);

  return { client, connected: status === "connected", status, volume, unlockAudio };
}
